import * as THREE from 'three';
import { SHIP } from '../utils/constants.js';
import { clamp } from '../utils/math.js';
import { events, EVT } from '../utils/events.js';

/**
 * Handles ship flight controls — enter/exit, cockpit, flight, warp.
 */
export class ShipController {
  constructor(ship, camera) {
    this.ship = ship;           // ShipModel
    this.camera = camera;
    this.isFlying = false;
    this.isLanded = true;
    this.isInShip = false;
    this.isInCockpit = false;

    // Flight state
    this.velocity = new THREE.Vector3();
    this.throttle = 0;
    this.boost = false;
    this._prevThrottle = 0;
    this._prevBoost = false;
    this.roll = 0;
    this.pitch = 0;
    this.yaw = 0;

    // For camera follow in flight
    this.cameraOffset = new THREE.Vector3(-12, 6, 0);
    this.cameraLookOffset = new THREE.Vector3(8, 0, 0);
  }

  enterShip() {
    this.isInShip = true;
    this.isInCockpit = false;
    this.ship.showInterior(true);
    events.emit(EVT.SHIP_ENTER);
  }

  exitShip() {
    this.boost = false;
    this.throttle = 0;
    events.emit(EVT.SHIP_THROTTLE, { throttle: 0, boost: false });
    this._prevThrottle = 0;
    this._prevBoost = false;
    this.isInShip = false;
    this.isInCockpit = false;
    this.ship.showInterior(false);
    events.emit(EVT.SHIP_EXIT);
  }

  sitInCockpit() {
    this.isInCockpit = true;
    // Move camera to pilot seat & look forward through windshield
    const seatWorld = this.ship.getPilotSeatWorld();
    this.camera.position.copy(seatWorld);
    const forward = new THREE.Vector3(1, 0, 0).applyQuaternion(this.ship.group.quaternion);
    this.camera.lookAt(seatWorld.clone().add(forward.multiplyScalar(10)));
  }

  leaveCockpit() {
    this.isInCockpit = false;
    if (this.isFlying) {
      // Can't leave cockpit while flying — land first
      return false;
    }
    // Move camera back to interior spawn
    const spawnWorld = this.ship.getInteriorSpawnWorld();
    this.camera.position.copy(spawnWorld);
    this.boost = false;
    this.throttle = 0;
    events.emit(EVT.SHIP_THROTTLE, { throttle: 0, boost: false });
    this._prevThrottle = 0;
    this._prevBoost = false;
    return true;
  }

  launch() {
    if (!this.isLanded) return;
    this.isLanded = false;
    this.isFlying = true;
    this.throttle = 0.5;
    events.emit(EVT.SHIP_LAUNCH);
  }

  land() {
    this.isFlying = false;
    this.isLanded = true;
    this.throttle = 0;
    this.boost = false;
    this.velocity.set(0, 0, 0);
    events.emit(EVT.SHIP_THROTTLE, { throttle: 0, boost: false });
    this._prevThrottle = 0;
    this._prevBoost = false;
    events.emit(EVT.SHIP_LAND);
  }

  handleInput(input, lookDelta = { dx: 0, dy: 0 }) {
    if (!this.isFlying) return;

    this.pitch = 0;
    this.yaw = 0;
    this.roll = 0;
    this._useLookSteering = false;

    const lookX = lookDelta.dx || 0;
    const lookY = lookDelta.dy || 0;
    const hasLookInput = Math.abs(lookX) > 1e-6 || Math.abs(lookY) > 1e-6;

    if (hasLookInput) {
      // Primary steering is through mouse/touch look, matching player movement.
      this._useLookSteering = true;
      this.yaw = -lookX * SHIP.LOOK_SPEED;
      this.pitch = -lookY * SHIP.LOOK_SPEED;
    } else {
      if (input.keys.has('KeyA') || input.keys.has('ArrowLeft')) this.yaw = 1;
      if (input.keys.has('KeyD') || input.keys.has('ArrowRight')) this.yaw = -1;
    }

    if (input.keys.has('KeyQ')) this.roll = -1;
    if (input.keys.has('KeyE')) this.roll = 1;

    const throttleInput = (input.keys.has('KeyW') ? 1 : 0)
      - (input.keys.has('KeyS') ? 1 : 0);
    if (throttleInput !== 0) {
      this.throttle = clamp(this.throttle + throttleInput * 0.02, 0, 1);
    } else if (input.keys.has('Space') || input.keys.has('ArrowUp')) {
      this.throttle = clamp(this.throttle + 0.02, 0, 1);
    } else if (input.keys.has('ArrowDown')) {
      this.throttle = clamp(this.throttle - 0.02, 0, 1);
    } else {
      this.throttle = clamp(this.throttle - 0.005, 0, 1);
    }

    this.boost = input.keys.has('ShiftLeft') || input.keys.has('ShiftRight') || input.keys.has('Action');
  }

  update(dt) {
    if (!this.isFlying) return;

    const shipGroup = this.ship.group;
    const rotSpeed = SHIP.ROTATION_SPEED * dt;

    if (this._useLookSteering) {
      shipGroup.rotateZ(this.pitch);
      shipGroup.rotateY(this.yaw);
    } else {
      shipGroup.rotateZ(this.pitch * rotSpeed);
      shipGroup.rotateY(this.yaw * rotSpeed);
    }
    shipGroup.rotateX(this.roll * rotSpeed * 0.5);

    const speed = this.boost ? SHIP.BOOST_SPEED : SHIP.SPEED;
    const forward = new THREE.Vector3(1, 0, 0).applyQuaternion(shipGroup.quaternion);
    const thrust = forward.multiplyScalar(this.throttle * speed * dt);

    this.velocity.add(thrust);
    this.velocity.multiplyScalar(0.98);
    shipGroup.position.add(this.velocity.clone().multiplyScalar(dt));

    this.ship.setEngineGlow(this.throttle * (this.boost ? 3 : 1.5));

    if (Math.abs(this.throttle - this._prevThrottle) > 0.0001 || this.boost !== this._prevBoost) {
      events.emit(EVT.SHIP_THROTTLE, { throttle: this.throttle, boost: this.boost });
    }
    if (this.boost && !this._prevBoost) {
      events.emit(EVT.SHIP_BOOST);
    }
    this._prevThrottle = this.throttle;
    this._prevBoost = this.boost;

    // Camera: 3rd person behind ship
    this._updateCamera(dt);
  }

  _updateCamera(dt) {
    const shipGroup = this.ship.group;
    const targetCamPos = this.cameraOffset.clone()
      .applyQuaternion(shipGroup.quaternion)
      .add(shipGroup.position);

    this.camera.position.lerp(targetCamPos, 5 * dt);

    const lookTarget = this.cameraLookOffset.clone()
      .applyQuaternion(shipGroup.quaternion)
      .add(shipGroup.position);

    this.camera.lookAt(lookTarget);
  }

  getShipWorldPosition() {
    return this.ship.group.getWorldPosition(new THREE.Vector3());
  }
}
